import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  DollarSign,
  TrendingUp,
  Package,
  Truck,
  Loader2
} from "lucide-react";
import OrderHistoryReport from "../components/reports/OrderHistoryReport";
import VendorReport from "../components/reports/VendorReport";
import UserReport from "../components/reports/UserReport";
import OutstandingOpenPosReport from "../components/reports/OutstandingOpenPosReport";

export default function Reports() {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");

  const [loading, setLoading] = useState(true);
  const [wonPos, setWonPos] = useState([]);
  const [allPOs, setAllPOs] = useState([]);
  const [activeTab, setActiveTab] = useState("financial");
  const [userRoles, setUserRoles] = useState([]);
  
  // Date states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [quickSelect, setQuickSelect] = useState("30"); // 7, 30, YOY, ALL, CUSTOM

  // Aggregated Data
  const [metrics, setMetrics] = useState({
    totalVendorCost: 0,
    totalShippingCost: 0,
    totalClientPrice: 0,
    totalProfit: 0,
    poCount: 0
  });
  const [filteredPoDetails, setFilteredPoDetails] = useState([]);

  // Subscribe to user roles
  useEffect(() => {
    if (!currentUser || !currentUser.email) return;
    const q = query(collection(db, "users"), where("email", "==", currentUser.email));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const userData = snapshot.docs[0].data();
        setUserRoles(userData.roles || []);
      }
    }, (err) => {
      console.error("Error loading user profile in Reports:", err);
    });
    return () => unsubscribe();
  }, [currentUser]);

  // Set active tab from URL query parameter if present
  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Fetch PO Data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, "pos"));
        const allPos = [];
        querySnapshot.forEach((doc) => {
          allPos.push({ id: doc.id, ...doc.data() });
        });

        setAllPOs(allPos);
        const won = allPos.filter(po => po.status === "Won");
        setWonPos(won);
        
        // Initialize date range to last 30 days
        handleQuickSelect("30", won);
      } catch (err) {
        console.error("Error fetching reports data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const getPoMarkupRate = (po) => {
    if (po.markupRate !== undefined && po.markupRate !== null) {
      return parseFloat(po.markupRate);
    }
    const savedMarkup = localStorage.getItem("global_markup_rate");
    return savedMarkup !== null ? parseFloat(savedMarkup) : 15;
  };

  const calculateMetrics = (posToCalculate, start, end) => {
    let vendorCost = 0;
    let shippingCost = 0;
    let clientPrice = 0;
    let count = 0;
    let detailsArray = [];

    const startObj = start ? new Date(start) : null;
    const endObj = end ? new Date(end) : null;
    if (endObj) endObj.setHours(23, 59, 59, 999); // Include entire end day

    posToCalculate.forEach(po => {
      const dateStr = po.awardedDate || po.date;
      if (!dateStr) return;
      
      const poDate = new Date(dateStr);
      
      if (startObj && poDate < startObj) return;
      if (endObj && poDate > endObj) return;

      count++;
      const markup = getPoMarkupRate(po);
      const items = po.items || [];
      
      let poVendorCost = 0;
      let poShippingCost = 0;
      let poClientPrice = 0;

      items.forEach(item => {
        const qty = Number(item.quantity || 0);
        const vPrice = Number(item.vendorPrice || 0);
        const sCost = Number(item.shippingCost || 0);
        
        const lineVendorCost = qty * vPrice;
        const lineTotalCost = lineVendorCost + sCost;
        const lineClientPrice = lineTotalCost * (1 + markup / 100);

        poVendorCost += lineVendorCost;
        poShippingCost += sCost;
        poClientPrice += lineClientPrice;

        vendorCost += lineVendorCost;
        shippingCost += sCost;
        clientPrice += lineClientPrice;
      });

      const poProfit = poClientPrice - (poVendorCost + poShippingCost);
      const poProfitMargin = poClientPrice > 0 ? (poProfit / poClientPrice) * 100 : 0;

      detailsArray.push({
        id: po.id,
        poNumber: po.poNumber,
        clientName: po.clientName,
        date: dateStr,
        vendorCost: poVendorCost,
        shippingCost: poShippingCost,
        clientPrice: poClientPrice,
        profit: poProfit,
        profitMargin: poProfitMargin,
        markup: markup
      });
    });

    detailsArray.sort((a, b) => new Date(b.date) - new Date(a.date));

    setFilteredPoDetails(detailsArray);
    setMetrics({
      totalVendorCost: vendorCost,
      totalShippingCost: shippingCost,
      totalClientPrice: clientPrice,
      totalProfit: clientPrice - (vendorCost + shippingCost),
      poCount: count
    });
  };

  const handleQuickSelect = (range, data = wonPos) => {
    setQuickSelect(range);
    const today = new Date();
    let startStr = "";
    let endStr = today.toISOString().split("T")[0];

    if (range === "7") {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      startStr = d.toISOString().split("T")[0];
    } else if (range === "30") {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      startStr = d.toISOString().split("T")[0];
    } else if (range === "YOY") {
      const d = new Date();
      d.setDate(d.getDate() - 365);
      startStr = d.toISOString().split("T")[0];
    } else if (range === "ALL") {
      startStr = "";
      endStr = "";
    }

    setStartDate(startStr);
    setEndDate(endStr);
    calculateMetrics(data, startStr, endStr);
  };

  const handleCustomDateChange = (start, end) => {
    setQuickSelect("CUSTOM");
    setStartDate(start);
    setEndDate(end);
    calculateMetrics(wonPos, start, end);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh", flexDirection: "column", gap: "1rem" }}>
        <Loader2 className="spinner-icon" size={32} style={{ color: "var(--primary)", animation: "spin 1s linear infinite" }} />
        <p style={{ color: "var(--text-secondary)" }}>Crunching numbers...</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in reports-container">
      <div className="page-header">
        <div className="page-header-info">
          <h1>
            {activeTab === "financial" 
              ? "Financial Reports" 
              : activeTab === "history" 
                ? "Order History" 
                : activeTab === "vendor"
                  ? "Vendor Analysis Reports"
                  : "User Performance Reports"}
          </h1>
          <p>
            {activeTab === "financial" 
              ? "Track profitability, procurement spend, and shipping costs across won bids." 
              : activeTab === "history"
                ? "Search, filter, and audit all Purchase Orders, logistics stages, and payment statuses."
                : activeTab === "vendor"
                  ? "Evaluate vendor performance, order frequencies, total purchase spend, and profit margins."
                  : "Track user line item counts, total procurement spend, and average margins."}
          </p>
        </div>
      </div>

      {/* Tabs navigation */}
      <div className="tab-container" style={{ display: "flex", gap: "1rem", borderBottom: "1px solid var(--border-color)", marginBottom: "1.5rem" }}>
        <button
          type="button"
          onClick={() => setActiveTab("financial")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "financial" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "financial" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          Financial Dashboard
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("history")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "history" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "history" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          Order History
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("vendor")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "vendor" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "vendor" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          Vendor Reports
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("user")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "user" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "user" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          User Reports
        </button>
        {(userRoles.includes("Manager") || userRoles.includes("Admin")) && (
          <button
            type="button"
            onClick={() => setActiveTab("outstanding_pos")}
            style={{
              background: "none",
              border: "none",
              borderBottom: activeTab === "outstanding_pos" ? "2px solid var(--primary)" : "2px solid transparent",
              color: activeTab === "outstanding_pos" ? "var(--primary)" : "var(--text-secondary)",
              padding: "0.5rem 1rem",
              fontSize: "0.95rem",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
          >
            Outstanding Open POs
          </button>
        )}
      </div>

      {activeTab === "financial" ? (
        <>
          <div className="reports-controls card">
            <div className="date-picker-group">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Start Date</label>
                <input 
                  type="date" 
                  className="form-input" 
                  value={startDate} 
                  onChange={(e) => handleCustomDateChange(e.target.value, endDate)}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">End Date</label>
                <input 
                  type="date" 
                  className="form-input" 
                  value={endDate} 
                  onChange={(e) => handleCustomDateChange(startDate, e.target.value)}
                />
              </div>
            </div>

            <div className="quick-select-group">
              <button 
                className={`btn ${quickSelect === "7" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => handleQuickSelect("7")}
              >
                Last 7 Days
              </button>
              <button 
                className={`btn ${quickSelect === "30" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => handleQuickSelect("30")}
              >
                Last 30 Days
              </button>
              <button 
                className={`btn ${quickSelect === "YOY" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => handleQuickSelect("YOY")}
              >
                Year over Year
              </button>
              <button 
                className={`btn ${quickSelect === "ALL" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => handleQuickSelect("ALL")}
              >
                All Time
              </button>
            </div>
          </div>

          <div className="metrics-grid">
            {/* Total Won Client Amount */}
            <div className="metric-card primary-gradient">
              <div className="metric-header">
                <h3>Total Procurement Won</h3>
                <div className="icon-box"><DollarSign size={20} /></div>
              </div>
              <div className="metric-value">
                ${metrics.totalClientPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="metric-footer">
                Based on Won PO awarded dates
              </div>
            </div>

            {/* Total Cost */}
            <div className="metric-card glass-card">
              <div className="metric-header">
                <h3>Total Cost of Goods</h3>
                <div className="icon-box-muted"><Package size={20} /></div>
              </div>
              <div className="metric-value small-val">
                ${(metrics.totalVendorCost + metrics.totalShippingCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="metric-footer" style={{ color: "var(--text-secondary)" }}>
                Vendor Cost: ${metrics.totalVendorCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            {/* Total Shipping */}
            <div className="metric-card glass-card">
              <div className="metric-header">
                <h3>Total Shipping Cost</h3>
                <div className="icon-box-muted"><Truck size={20} /></div>
              </div>
              <div className="metric-value small-val">
                ${metrics.totalShippingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="metric-footer" style={{ color: "var(--text-secondary)" }}>
                Across {metrics.poCount} shipments
              </div>
            </div>

            {/* Net profit */}
            <div className="metric-card glass-card">
              <div className="metric-header">
                <h3>Net Estimated Profit</h3>
                <div className="icon-box-muted"><TrendingUp size={20} /></div>
              </div>
              <div className="metric-value small-val" style={{ color: metrics.totalProfit >= 0 ? "var(--success)" : "var(--danger)" }}>
                ${metrics.totalProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="metric-footer" style={{ color: "var(--text-secondary)" }}>
                Average Margin: {metrics.poCount > 0 ? (metrics.totalProfit / metrics.totalClientPrice * 100 || 0).toFixed(1) : 0}%
              </div>
            </div>
          </div>

          <div className="reports-table-container">
            <h3>Won PO Bids Listing ({metrics.poCount})</h3>
            <div className="table-responsive">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th style={{ paddingLeft: "1rem" }}>PO Number</th>
                    <th>Awarded Date</th>
                    <th>Customer</th>
                    <th className="text-right">Vendor Cost</th>
                    <th className="text-right">Shipping Cost</th>
                    <th className="text-right">Client Price</th>
                    <th className="text-right">Profit</th>
                    <th className="text-right">Markup</th>
                    <th className="text-right">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPoDetails.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="empty-state">No won POs found in selected range.</td>
                    </tr>
                  ) : (
                    filteredPoDetails.map((detail) => (
                      <tr key={detail.id}>
                        <td style={{ paddingLeft: "1rem", fontWeight: 600, color: "var(--text-primary)" }}>
                          {detail.poNumber}
                        </td>
                        <td>{detail.date}</td>
                        <td>{detail.clientName}</td>
                        <td className="text-right">
                          ${detail.vendorCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="text-right">
                          ${detail.shippingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="text-right" style={{ color: "var(--primary)", fontWeight: 600 }}>
                          ${detail.clientPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="text-right" style={{ color: "var(--success)", fontWeight: 600 }}>
                          ${detail.profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="text-right">
                          {detail.markup.toFixed(1)}%
                        </td>
                        <td className="text-right">
                          <span className={`profit-badge ${detail.profitMargin >= 20 ? 'high-margin' : detail.profitMargin >= 10 ? 'med-margin' : 'low-margin'}`}>
                            {detail.profitMargin.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : activeTab === "history" ? (
        <OrderHistoryReport />
      ) : activeTab === "vendor" ? (
        <VendorReport />
      ) : activeTab === "outstanding_pos" ? (
        <OutstandingOpenPosReport pos={allPOs} />
      ) : (
        <UserReport />
      )}

      <style>{`
        .reports-container {
          padding-bottom: 3rem;
        }

        .reports-controls {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          flex-wrap: wrap;
          gap: 1.5rem;
          margin-bottom: 2rem;
          padding: 1.5rem;
          background-color: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
        }

        .date-picker-group {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .quick-select-group {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .metric-card {
          padding: 1.5rem;
          border-radius: var(--radius-md);
          display: flex;
          flex-direction: column;
          gap: 1rem;
          position: relative;
          overflow: hidden;
        }

        .metric-card::after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 150px;
          height: 150px;
          background: linear-gradient(135deg, transparent 50%, rgba(255, 255, 255, 0.05) 50%);
          border-radius: 50%;
          transform: translate(30%, -30%);
          pointer-events: none;
        }

        .primary-gradient {
          background: linear-gradient(135deg, hsl(var(--hue-primary), 85%, 60%), hsl(var(--hue-primary), 85%, 45%));
          border: none;
          color: white;
          box-shadow: 0 8px 16px hsla(var(--hue-primary), 85%, 45%, 0.2);
        }

        .success-gradient {
          background: linear-gradient(135deg, hsl(var(--hue-success), 70%, 50%), hsl(var(--hue-success), 70%, 35%));
          border: none;
          color: white;
          box-shadow: 0 8px 16px hsla(var(--hue-success), 70%, 35%, 0.2);
        }

        .standard-card {
          background-color: var(--bg-card);
          border: 1px solid var(--border-color);
          box-shadow: var(--shadow-sm);
        }

        .metric-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .metric-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          opacity: 0.9;
        }

        .icon-box {
          background: rgba(255, 255, 255, 0.2);
          padding: 0.5rem;
          border-radius: 8px;
          display: flex;
        }

        .icon-box-muted {
          background: var(--bg-input);
          color: var(--primary);
          padding: 0.5rem;
          border-radius: 8px;
          display: flex;
        }

        .metric-value {
          font-family: var(--font-heading);
          font-size: 2.5rem;
          font-weight: 800;
          letter-spacing: -0.02em;
        }

        .small-val {
          font-size: 1.75rem;
          color: var(--text-primary);
        }

        .metric-footer {
          font-size: 0.85rem;
          opacity: 0.8;
          font-weight: 500;
        }

        .reports-table-container {
          margin-top: 2rem;
          background-color: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: 1.5rem;
          box-shadow: var(--shadow-sm);
        }

        .reports-table-container h3 {
          margin-top: 0;
          margin-bottom: 1.25rem;
          font-size: 1.2rem;
          color: var(--text-primary);
        }

        .table-responsive {
          overflow-x: auto;
        }

        .reports-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
        }

        .reports-table th {
          text-align: left;
          padding: 0.75rem 1rem;
          background-color: var(--bg-input);
          color: var(--text-secondary);
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid var(--border-color);
        }

        .reports-table td {
          padding: 1rem;
          border-bottom: 1px solid var(--border-color);
          color: var(--text-primary);
        }

        .reports-table tr:hover td {
          background-color: hsla(var(--hue-primary), 89%, 60%, 0.05);
        }

        .reports-table tr:last-child td {
          border-bottom: none;
        }

        .text-right {
          text-align: right !important;
        }

        .font-heading {
          font-family: var(--font-heading);
        }

        .empty-state {
          text-align: center;
          padding: 3rem !important;
          color: var(--text-muted) !important;
        }

        .profit-badge {
          display: inline-block;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 700;
        }

        .high-margin {
          background-color: hsla(var(--hue-success), 70%, 50%, 0.2);
          color: var(--success);
        }

        .med-margin {
          background-color: hsla(var(--hue-primary), 85%, 60%, 0.15);
          color: var(--primary);
        }

        .low-margin {
          background-color: rgba(255, 165, 0, 0.2);
          color: orange;
        }
      `}</style>
    </div>
  );
}
